import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import PhoneNumberInput from "components/common/PhoneInput";
import useLocalizationStore from "store/zustand/localization";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const UserInfo = ({ show }) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { language } = useLocalizationStore();
  const firstName = watch("company_owner_first_name"); // Access value for first_name
  const lastName = watch("company_owner_last_name"); // Access value for last_name
  const email = watch("company_owner_email"); // Access value for first_name
  const number = watch("company_owner_mobile"); // Access value for last_name
  const [showPassword, setShowPassword] = useState(false); // To toggle password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // To toggle confirm password visibility

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword((prev) => !prev);
  };
  useEffect(() => {
    // Dynamically set values if you want to set the default values for first_name and last_name
    setValue("first_name", firstName || ""); // Set default value if not available
    setValue("last_name", lastName || ""); // Set default value if not available
    setValue("email", email || ""); // Set default value if not available
    setValue("mobile_number", number || ""); // Set default value if not available
  }, [firstName, lastName, setValue, number, email]);

  return (
    <Box sx={{ mt: show ? 0 : 3 }}>
      {!show && (
        <Typography variant="h6">
          {t("auth.signup.sections.adminInfo")}
        </Typography>
      )}
      <Paper
        elevation={show ? 0 : 1}
        sx={{ bgcolor: "transparent", p: 2, mb: 2 }}
      >
        <Grid container spacing={2}>
          {/* First Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.adminFirstName.label")}
              fullWidth
              {...register("first_name", {
                required: t("auth.common.first_name_required"),
              })}
              error={Boolean(errors.first_name)}
              helperText={errors.first_name?.message}
            />
          </Grid>

          {/* Last Name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.adminLastName.label")}
              fullWidth
              {...register("last_name", {
                required: t("auth.common.last_name_required"),
              })}
              error={Boolean(errors.last_name)}
              helperText={errors.last_name?.message}
            />
          </Grid>

          {/* Email */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.common.emailLabel")}
              fullWidth
              type="email"
              {...register("email", {
                required: t("auth.common.email_required"),
              })}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          </Grid>

          {/* Mobile Number */}
          <Grid size={{ xs: 12, md: 6 }}>
            <PhoneNumberInput
              required={true}
              field={{
                ...register("mobile_number", {
                  required: t("auth.common.phone_number_required"),
                }),
                value: watch("mobile_number") || "",
              }}
              error={errors.mobile_number?.message}
              onChange={(e) => setValue("mobile_number", e)} // Proper onChange handler to update value
            />
          </Grid>

          {/* Password */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.common.passwordLabel")}
              fullWidth
              type={showPassword ? "text" : "password"}
              {...register("password", {
                required: t("auth.common.password_required"),
                minLength: {
                  value: 8,
                  message: t("auth.common.password_min"),
                },
                maxLength: {
                  value: 32,
                  message: t("auth.common.password_max"),
                },
                validate: {
                  hasUppercase: (v) =>
                    /[A-Z]/.test(v) || t("auth.common.password_upper_case"),
                  hasLowercase: (v) =>
                    /[a-z]/.test(v) || t("auth.common.password_lower_case"),
                  hasNumber: (v) =>
                    /\d/.test(v) || t("auth.common.password_number"),
                  hasSpecial: (v) =>
                    /[@$!%*?&#]/.test(v) || t("auth.common.password_special"),
                },
              })}
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              InputProps={{
                [language === "en" ? "endAdornment" : "startAdornment"]: (
                  <InputAdornment
                    position={language === "en" ? "end" : "start"}
                  >
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {!showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Confirm Password */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t("auth.signup.fields.confirmPassword.label")}
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              {...register("confirm_password", {
                required: t("auth.signup.fields.confirmPassword.required"),
                validate: (value) =>
                  value === watch("password") ||
                  t("auth.common.passwords_must_match"),
              })}
              error={Boolean(errors.confirm_password)}
              helperText={errors.confirm_password?.message}
              InputProps={{
                [language === "en" ? "endAdornment" : "startAdornment"]: (
                  <InputAdornment
                    position={language === "en" ? "end" : "start"}
                  >
                    <IconButton
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {!showConfirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default UserInfo;
